import clsx from 'clsx'
import map from 'lodash/map'
import { GetServerSideProps } from 'next'
import React, { useContext, useEffect, useState } from 'react'

import SvgCashMultiple from '@Assets/SVG/CashMultiple'
import SvgClubTrendhim from '@Assets/SVG/ClubTrendhim'
import { CMSPage } from '@Components/CMS/Page'
import { LoadingSpinner } from '@Components/Loading'
import { Wordbook } from '@Components/Wordbook'
import { ClubInfo } from '@Domains/Club/Components/ClubInfo'
import { useClub } from '@Domains/Club/Hook'
import { Page, PageType, useMeWithClubQuery } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { MetadataContext } from '@Globals/Metadata'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { useTranslation } from '@Globals/Translations'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

import styles from './AccountOverview.module.scss'

interface AccountOverviewProps extends PageProps {
  item: Page
}

export const getServerSideProps: GetServerSideProps<AccountOverviewProps> = async (context) => {
  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(
    apolloClient,
    PageType.Account,
    '/account/overview',
    undefined,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  return {
    props: {
      ...await pageProps(
        context,
        { type: 'accounts', id: page.id || '' },
        page,
        ['cart', 'common', 'header', 'footer', 'shipping', 'cms', 'club', 'sidebar', 'products'],
        true,
      ),
      item: page as Page,
    },
  }
}

const AccountOverview = PageHOC<AccountOverviewProps>(({ item }) => {
  const components = useBlockComponents(item.blocks || [])
  const { t, tDanger } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const { isReady, logout, setModalState } = useClub()
  const { storeURL } = useContext(MetadataContext)
  const [loggingOut, setIsLoggingOut] = useState(false)
  const [initialRender, setInitialRender] = useState(true)
  const { data, error, loading } = useMeWithClubQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
      withAuth: true,
    },
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (loading) {
      return
    }

    if (!error && data?.me?.__typename === 'Customer') {
      return
    }

    if (loggingOut) {
      return
    }

    if (!isReady) {
      return
    }

    setModalState((prev) => {
      if (prev !== 'CLOSED') {
        return prev
      }

      return 'EMAIL_DECISION'
    })
    setInitialRender(false)
  }, [error, isReady, loading, loggingOut, setModalState, data?.me?.__typename])

  if (data?.me?.__typename !== 'Customer' || error) {
    if (initialRender || loading) {
      return (
        <CMSPage>
          <div className={styles.closedLogin}>
            <SvgClubTrendhim className={styles.logo} />
            <div className={styles.buttons}>
              <LoadingSpinner />
            </div>
          </div>
        </CMSPage>
      )
    }

    if (!initialRender) {
      return (
        <CMSPage>
          <div className={styles.closedLogin}>
            <SvgClubTrendhim className={styles.logo} />
            <div className={styles.buttons}>
              <button
                className={styles.join}
                onClick={() => setModalState('EMAIL_DECISION')}
                type="button"
              >
                {t('common:TRENDHIMCLUB_JOIN_LABEL', 'JOIN')}
              </button>
              <button
                className={styles.login}
                onClick={() => setModalState('EMAIL_DECISION')}
                type="button"
              >
                {t('common:TRENDHIMCLUB_LOG_IN_LABEL', 'LOGIN')}
              </button>
            </div>
          </div>
        </CMSPage>
      )
    }

    return null
  }

  const history = data.me.club?.history || []

  return (
    <CMSPage>
      <div className={styles.accountOverview}>
        <div className={styles.accountBalance}>
          <SvgClubTrendhim />
          <p className={styles.feelingText} {...tDanger('club:TRENDHIMCLUB_A_STRONG_CLUB_TEXT')} />
          <div className={styles.boxWrap} style={{ minHeight: '300px' }}>
            <p className={styles.textBalanceHeader}>{t('club:TRENDHIMCLUB_CASHBACK_BALANCE')}</p>
            <span>
              <SvgCashMultiple />
              <p className={styles.textBalance}>{data.me.club?.balance?.format || '0'}</p>
            </span>
            <p className={styles.availableInCart}>{t('club:TRENDHIMCLUB_AVAILABLE_IN_YOUR_CART')}</p>
          </div>
          {data.me.club?.expiringTransaction?.amount.format && data.me.club?.expiringTransaction.expiresIn && (
            <div className={styles.boxWrap}>
              <p className={styles.expiresSoonHeader}>{t('club:TRENDHIMCLUB_EXPIRING_SOON')}</p>
              <p className={styles.expiresSoonValue}>{`${data.me.club?.expiringTransaction?.amount.format}`}</p>
              <p className={styles.expiresSoonTime}>
                <Wordbook wordbook={data.me.club.expiringTransaction.expiresIn} />
              </p>
            </div>
          )}
          <button
            className={styles.logout}
            onClick={async () => {
              setIsLoggingOut(true)
              await logout()
              window.location.href = storeURL('')
            }}
            type="button"
          >
            {t('club:LOG_OUT_LABEL')}
          </button>
        </div>
        <div className={styles.accountContent}>
          <div className={styles.ClubInfoWrapper}>
            <h3>{t('club:TRENDHIMCLUB_YOUR_BENEFITS')}</h3>
            <ClubInfo className={styles.infoItems} itemClassName={styles.infoItem} />
          </div>
          <div className={styles.cashBacksListWrapper}>
            <h3>{t('club:TRENDHIMCLUB_OVERVIEW_OF_YOUR_ORDERS')}</h3>
            <div className={styles.cashBacksList}>
              {history.length === 0 && (
                <div key="no_orders" className={styles.cashBack}>
                  <p className={clsx(styles.orderNumber, styles.noOrders)}>
                    {t('club:TRENDHIMCLUB_NO_ORDERS_YET')}
                  </p>
                </div>
              )}
              {map((showAll ? history : history?.slice(0, 3)), transaction => (
                <div key={transaction.orderNumber} className={styles.cashBack}>
                  <p className={styles.orderNumber}>
                    {`${t('club:ORDER_NUMBER')} ${transaction.orderNumber}`}
                  </p>
                  <p className={styles.orderPlaced}>{transaction.createdAtText}</p>
                  <p className={styles.cashbackHeader}>
                    {t('club:TRENDHIMCLUB_AMOUNT_CASHBACK', { amount: '' })}
                  </p>
                  <p className={styles.cashbackEarned}>
                    {transaction.amount.format}
                  </p>
                </div>
              ))}
              {!showAll && history?.length > 3 && (
                <button className={styles.viewMore} onClick={() => setShowAll(true)} type="button">
                  {t('club:TRENDHIMCLUB_VIEW_MORE')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {components}
    </CMSPage>
  )
})

export default AccountOverview
